import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getBookingsReport, getStaff, getAgencies } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { DownloadSimple, FileXls, FilePdf, CalendarBlank } from '@phosphor-icons/react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';

export default function ReportsPage() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [selectedAgency, setSelectedAgency] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [agencies, setAgencies] = useState([]);

  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const isAdminOrAbove = isOwner || user?.role === 'assistant';
  const isAdmin = isAdminOrAbove;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const staffId = selectedStaff === 'all' ? undefined : selectedStaff;
      const agencyId = selectedAgency === 'all' ? undefined : selectedAgency;
      const sd = startDate || undefined;
      const ed = endDate || undefined;
      const { data } = await getBookingsReport(staffId, sd, ed, agencyId);
      setBookings(data);
      
      if (isAdmin) {
        const [staffRes, agRes] = await Promise.all([getStaff(), getAgencies()]);
        setStaffList(staffRes.data);
        setAgencies(agRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedStaff, startDate, endDate, selectedAgency]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!isAdminOrAbove) {
    return <Navigate to="/" replace />;
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const exportToExcel = () => {
    const data = bookings.map(b => ({
      'Staff': b.staff_name,
      'Agency': b.agency_name,
      'Customer': b.customer_name || '-',
      'Amount': b.amount,
      'Convenience Fee': b.convenience_fee,
      'Total': b.total_amount,
      'Source': b.source,
      'Status': b.status,
      'Date': b.date
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bookings Report');
    
    // Generate buffer
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `LPG_Bookings_Report_${new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })}.xlsx`);
    toast.success('Excel report downloaded');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text('LPG Bookings Report', 14, 22);
    doc.setFontSize(10);
    doc.text(`Generated: ${formatDateTime(new Date())}`, 14, 30);
    
    // Table headers
    const headers = ['Staff', 'Agency', 'Amount', 'Fee', 'Total', 'Source', 'Status'];
    let y = 40;
    
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    headers.forEach((h, i) => {
      doc.text(h, 14 + (i * 27), y);
    });
    
    // Table data
    doc.setFont(undefined, 'normal');
    bookings.slice(0, 30).forEach((b, idx) => {
      y += 7;
      if (y > 280) {
        doc.addPage();
        y = 20;
      }
      doc.text(b.staff_name.substring(0, 10), 14, y);
      doc.text(b.agency_name.substring(0, 10), 41, y);
      doc.text(b.amount.toString(), 68, y);
      doc.text(b.convenience_fee.toFixed(2), 95, y);
      doc.text(b.total_amount.toFixed(2), 122, y);
      doc.text(b.source, 149, y);
      doc.text(b.status, 176, y);
    });
    
    // Summary
    const totalAmount = bookings.reduce((sum, b) => sum + b.total_amount, 0);
    const totalFee = bookings.reduce((sum, b) => sum + b.convenience_fee, 0);
    
    y += 15;
    doc.setFont(undefined, 'bold');
    doc.text(`Total Bookings: ${bookings.length}`, 14, y);
    doc.text(`Total Amount: Rs. ${totalAmount.toFixed(2)}`, 14, y + 7);
    doc.text(`Total Convenience Fee: Rs. ${totalFee.toFixed(2)}`, 14, y + 14);
    
    doc.save(`LPG_Bookings_Report_${new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })}.pdf`);
    toast.success('PDF report downloaded');
  };

  const getSourceBadge = (source) => {
    if (source === 'tataneu') return <span className="badge-tataneu">TataNeu</span>;
    if (source === 'amazon') return <span className="badge-amazon">Amazon</span>;
    return <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs">{source}</span>;
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') return <span className="badge-success">Completed</span>;
    if (status === 'refunded') return <span className="badge-warning">Refunded</span>;
    return <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs">{status}</span>;
  };

  // Summary calculations
  const totalAmount = bookings.reduce((sum, b) => sum + b.total_amount, 0);
  const totalFee = bookings.reduce((sum, b) => sum + b.convenience_fee, 0);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <div className="flex items-center gap-3 flex-wrap">
          {isAdmin && (
            <SearchableSelect
              options={[
                { value: 'all', label: 'All Staff' },
                ...staffList.map(s => ({ value: s.id, label: s.name, sublabel: s.email }))
              ]}
              value={selectedStaff}
              onValueChange={setSelectedStaff}
              placeholder="Select Staff"
              searchPlaceholder="Search staff..."
              className="w-48"
              data-testid="staff-filter-reports"
            />
          )}
          <SearchableSelect
            options={[
              { value: 'all', label: 'All Agencies' },
              ...agencies.map(a => ({ value: a.id, label: a.name, sublabel: a.oil_company || '' }))
            ]}
            value={selectedAgency}
            onValueChange={setSelectedAgency}
            placeholder="Select Agency"
            searchPlaceholder="Search agency..."
            className="w-52"
            data-testid="agency-filter-reports"
          />
          <div className="flex items-center gap-1.5">
            <CalendarBlank size={16} className="text-gray-400" />
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-9 w-36 text-sm" data-testid="reports-start-date" />
            <span className="text-gray-400 text-xs">to</span>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-9 w-36 text-sm" data-testid="reports-end-date" />
            {(startDate || endDate) && (
              <Button variant="ghost" size="sm" onClick={() => { setStartDate(''); setEndDate(''); }} className="text-xs text-gray-500 h-8 px-2" data-testid="reports-clear-dates">
                Clear
              </Button>
            )}
          </div>
          <Button onClick={exportToExcel} className="btn-secondary" data-testid="export-excel-btn">
            <FileXls size={18} className="mr-2 text-green-600" /> Excel
          </Button>
          <Button onClick={exportToPDF} className="btn-secondary" data-testid="export-pdf-btn">
            <FilePdf size={18} className="mr-2 text-red-600" /> PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card" data-testid="report-total-bookings">
          <div className="stat-label">Total Bookings</div>
          <div className="stat-value">{bookings.length}</div>
        </div>
        <div className="stat-card" data-testid="report-total-amount">
          <div className="stat-label">Total Amount</div>
          <div className="stat-value">{formatCurrency(totalAmount)}</div>
        </div>
        <div className="stat-card" data-testid="report-total-fee">
          <div className="stat-label">Total Convenience Fee</div>
          <div className="stat-value text-[#E90069]">{formatCurrency(totalFee)}</div>
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : (
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead>Staff</TableHead>
                <TableHead>Agency</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Fee</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-gray-500">
                    No bookings found
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((b, idx) => (
                  <TableRow key={b.id || idx} data-testid={`report-row-${idx}`}>
                    <TableCell className="font-medium">{b.staff_name}</TableCell>
                    <TableCell>{b.agency_name}</TableCell>
                    <TableCell className="text-gray-500">{b.customer_name || '-'}</TableCell>
                    <TableCell className="numeric">{formatCurrency(b.amount)}</TableCell>
                    <TableCell className="numeric text-[#E90069]">{formatCurrency(b.convenience_fee)}</TableCell>
                    <TableCell className="numeric font-semibold">{formatCurrency(b.total_amount)}</TableCell>
                    <TableCell>{getSourceBadge(b.source)}</TableCell>
                    <TableCell>{getStatusBadge(b.status)}</TableCell>
                    <TableCell>{b.date}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
